/**
 * Base domains that bypass the leave-site modal. A URL matches if its hostname is
 * the apex host `<domain>` or any subdomain of `<domain>` (any path or query).
 */
export const LEAVE_SITE_WHITELISTED_DOMAINS = {
  SALESFORCE: "salesforce.com",
  SLACK: "slack.com",
  TABLEAU: "tableau.com",
  INFORMATICA: "informatica.com",
  MULESOFT: "mulesoft.com",
  OWNDATA: "owndata.com",
  HEROKU: "heroku.com",
  SPIFF: "spiff.com",
  ZOOMINSOFTWARE: "zoominsoftware.com",
} as const;

const whitelistedDomainSet = new Set<string>(
  Object.values(LEAVE_SITE_WHITELISTED_DOMAINS)
);

/**
 * Returns the URL hostname in lowercase, or "" if parsing fails.
 * Prepends https:// when no scheme is present so bare domains and hostnames parse correctly.
 */
export function getLeaveSiteHostname(url: string): string {
  if (url == null) return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    return new URL(withProtocol).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function hostnameMatchesWhitelistedDomain(
  hostname: string,
  domain: string
): boolean {
  const h = hostname.toLowerCase();
  const d = domain.toLowerCase();
  return h === d || h.endsWith(`.${d}`);
}

/**
 * Returns true if the URL is whitelisted and should open without showing the leave-site modal.
 */
export function isWhitelistedLeaveSiteUrl(url: string): boolean {
  const hostname = getLeaveSiteHostname(url);
  if (!hostname) return false;
  for (const domain of whitelistedDomainSet) {
    if (hostnameMatchesWhitelistedDomain(hostname, domain)) {
      return true;
    }
  }
  return false;
}

export const REQUEST_LEAVE_SITE_MODAL_EVENT = "tbme_requestleavesitemodal";

/**
 * Request to leave the site for the given href.
 * If the URL is whitelisted, opens in a new tab immediately.
 * Otherwise dispatches an event so the leave-site modal shows.
 */
export function requestLeaveSite(href: string): void {
  if (isWhitelistedLeaveSiteUrl(href)) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }
  document.dispatchEvent(
    new CustomEvent(REQUEST_LEAVE_SITE_MODAL_EVENT, {
      detail: { href },
      bubbles: true,
      composed: true,
    })
  );
}
